import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, concat, defer, iif, Observable, of, Subject, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ProductQuoteResponse } from '../../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { PremiumRequest } from '../../../../../../common/js/productService/ProductServiceClient';
import { Product, RiderType } from '../../../../../../common/js/static/Product';
import { RateClasses } from '../../../../../../common/js/static/RateClasses';
import { Controller } from '../../../Controller';
import { AgentAmorphicService } from '../../../services/amorphic/amorphic.service';
import { Utils } from '../../../../../../common/js/Utils';
import { CommonUtils } from '../../../../../../common/js/static/CommonUtils';
import moment from 'moment';
import { InitialInvoice } from '../../../../../../common/js/policyAdmin/interface/InitialInvoiceInfoResponse';
import type { CoverpathPolicy } from '../../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { PolicyAdminRequestPartialConversionQuotePayload } from '../../../../../../common/js/policyAdmin/PolicyAdminClient';
import { POLICY_TYPES } from '../../../../../../common/js/constants/policy';

@Injectable()
export class RecalculationService {
  private readonly controller: Controller;

  /**
   * Keeps track of current loading status.
   * BehaviorSubject is used so that new subscribers also get latest loading status
   * */
  private readonly _loading$ = new BehaviorSubject<boolean>(false);
  readonly loading$ = this._loading$.asObservable();
  readonly loadingPremiumSolve$ = new Subject<boolean>();

  private readonly defaultPremium: ProductQuoteResponse = {
    base: 0,
    waiver: 0,
    riders: [
      { type: 'GIR', amount: 0, premium: 0, waiver: 0 },
      { type: 'RTR', amount: 0, premium: 0, waiver: 0 },
    ],
    totalTargetPremium: 0,
    annualBase: 0,
  };
  private readonly initialInvoice: InitialInvoice = { amount: 0, coverageEndDate: null, coverageStartDate: null };

  constructor(private amorphicService: AgentAmorphicService) {
    this.controller = this.amorphicService.controller as Controller;
  }

  private isNullOrUndefined(...values: unknown[]) {
    return values.some((e) => e === null || e === undefined);
  }

  private validateWhole([
    dob,
    gender,
    healthClass,
    contractState,
    product,
    faceAmount,
    paymentFrequency,
    waiverOfPremium,
    girAmount,
    hasGirRider,
    rtrAmount,
    hasRtrRider,
    alirAmount,
    hasAlirRider,
    alirYears,
    alirPaymentSchedule,
    hasCirRider,
    cirBaseBenefitPool,
  ]: object[]): boolean {
    if (this.isNullOrUndefined(dob, gender, healthClass, contractState, product, faceAmount, paymentFrequency)) {
      return false;
    }

    if (hasGirRider && this.isNullOrUndefined(girAmount)) {
      return false;
    }

    if (hasRtrRider && this.isNullOrUndefined(rtrAmount)) {
      return false;
    }

    if (
      hasAlirRider &&
      (this.isNullOrUndefined(alirAmount) ||
        this.isNullOrUndefined(alirYears) ||
        this.isNullOrUndefined(alirPaymentSchedule))
    ) {
      return false;
    }

    if (hasCirRider && this.isNullOrUndefined(cirBaseBenefitPool)) {
      return false;
    }

    return true;
  }

  private validateTermPremiumSolve([
    dob,
    gender,
    healthClass,
    contractState,
    product,
    paymentFrequency,
    waiverOfPremium,
    ecp,
    coverageTerm,
    dummy1, // these dummy entries maintain proper array alignment because we're using an array and not an interface or class
    dummy2,
    dummy3,
    dummy4,
    dummy5,
    dummy6,
    uwFlatExtras,
    type,
    effectiveDate,
    quoteHealthClass,
    productPremiumVersion,
    desiredPremium,
  ]: object[]): boolean {
    if (
      this.isNullOrUndefined(
        dob,
        gender,
        healthClass,
        contractState,
        coverageTerm,
        ecp,
        paymentFrequency,
        desiredPremium
      )
    ) {
      return false;
    }

    if (parseInt(desiredPremium.toString()) === 0) {
      return false;
    }

    return true;
  }

  private validateWholePremiumSolve([
    dob,
    gender,
    healthClass,
    contractState,
    product,
    paymentFrequency,
    waiverOfPremium,
    girAmount,
    hasGirRider,
    rtrAmount,
    hasRtrRider,
    alirAmount,
    hasAlirRider,
    alirYears,
    alirPaymentSchedule,
    flatExtras,
    type,
    effectiveDate,
    wholeLifeHealthClass,
    productPremiumVersion,
    desiredPremium,
  ]: object[]): boolean {
    if (this.isNullOrUndefined(desiredPremium)) {
      return false;
    }

    if (this.isNullOrUndefined(dob, gender, healthClass, contractState, product, paymentFrequency)) {
      return false;
    }

    if (hasGirRider && this.isNullOrUndefined(girAmount)) {
      return false;
    }

    if (hasRtrRider && this.isNullOrUndefined(rtrAmount)) {
      return false;
    }

    if (
      hasAlirRider &&
      (this.isNullOrUndefined(alirAmount) ||
        this.isNullOrUndefined(alirYears) ||
        this.isNullOrUndefined(alirPaymentSchedule))
    ) {
      return false;
    }

    if (parseInt(desiredPremium.toString()) === 0) {
      return false;
    }

    return true;
  }

  private validateTerm([
    dob,
    gender,
    healthClass,
    contractState,
    coverageTerm,
    faceAmount,
    ecp,
    paymentFrequency,
    waiverOfPremium,
  ]: object[]): boolean {
    if (
      this.isNullOrUndefined(dob, gender, healthClass, contractState, coverageTerm, faceAmount, ecp, paymentFrequency)
    ) {
      return false;
    }

    if (parseInt(faceAmount.toString()) === 0) {
      return false;
    }

    return true;
  }

  createTermPremium$(clientInformation: AbstractControl, termQuote: AbstractControl): Observable<ProductQuoteResponse> {
    // controls on which premium calculation is dependent. order is important.
    const controls = [
      clientInformation.get('dob'),
      clientInformation.get('gender'),
      clientInformation.get('healthClass'),
      clientInformation.get('contractState'),
      termQuote.get('coverageTerm'),
      termQuote.get('faceAmount'),
      termQuote.get('ecp'),
      termQuote.get('paymentFrequency'),
      termQuote.get('waiverOfPremium'),
      termQuote.get('product'),
      termQuote.get('uwFlatExtras'),
      termQuote.get('type'),
      termQuote.get('effectiveDate'),
      termQuote.get('healthClass'),
      termQuote.get('productPremiumVersion'),
    ];

    // map each control to an observable
    const observables = controls.map((control) =>
      combineLatest(
        control.valueChanges.pipe(startWith(control.value)),
        control.statusChanges.pipe(startWith('VALID'))
      ).pipe(
        debounceTime(500),
        map(([value, status]) => (status === 'VALID' || status === 'DISABLED' ? value : undefined))
      )
    );

    return combineLatest(observables).pipe(
      // no need to fetch term premium if term quote is disabled
      filter(() => termQuote.enabled),
      switchMap((values) =>
        iif(
          () => this.validateTerm(values),
          defer(() => this.fetchTermPremium(values)),
          of(this.defaultPremium)
        )
      )
    );
  }

  createTermPremiumReprice$(
    clientInformation: AbstractControl,
    termQuote: AbstractControl,
    previewTermRepriceDate: Date,
    productPremiumVersionOverride: string
  ): Observable<ProductQuoteResponse> {
    // controls on which premium calculation is dependent. order is important.
    const controls = [
      clientInformation.get('dob'),
      clientInformation.get('gender'),
      clientInformation.get('healthClass'),
      clientInformation.get('contractState'),
      termQuote.get('coverageTerm'),
      termQuote.get('faceAmount'),
      termQuote.get('ecp'),
      termQuote.get('paymentFrequency'),
      termQuote.get('waiverOfPremium'),
      termQuote.get('product'),
      termQuote.get('uwFlatExtras'),
      termQuote.get('type'),
      termQuote.get('effectiveDate'),
      termQuote.get('healthClass'),
      termQuote.get('productPremiumVersion'),
    ];

    // map each control to an observable
    const observables = controls.map((control) =>
      combineLatest(
        control.valueChanges.pipe(startWith(control.value)),
        control.statusChanges.pipe(startWith('VALID'))
      ).pipe(
        debounceTime(500),
        map(([value, status]) => (status === 'VALID' || status === 'DISABLED' ? value : undefined))
      )
    );

    return combineLatest(observables).pipe(
      // no need to fetch term premium if term quote is disabled
      filter(() => termQuote.enabled),
      switchMap((values) =>
        iif(
          () => this.validateTerm(values),
          defer(() => this.fetchTermPremium(values, previewTermRepriceDate, productPremiumVersionOverride)),
          of(this.defaultPremium)
        )
      )
    );
  }

  createTermPartialConversionPremium$(
    termPolicy: CoverpathPolicy,
    termQuote: AbstractControl,
    paFaceAmount: number
  ): Observable<ProductQuoteResponse> {
    const controls: AbstractControl[] = [termQuote.get('faceAmount')];

    const observables = controls.map((control) =>
      combineLatest(
        control.valueChanges.pipe(startWith(control.value)),
        control.statusChanges.pipe(startWith('VALID'))
      ).pipe(
        debounceTime(500),
        map(([value, status]) => (status === 'VALID' || status === 'DISABLED' ? value : undefined)),
        distinctUntilChanged()
      )
    );

    return combineLatest(observables).pipe(
      switchMap((values) =>
        iif(
          () => !this.isNullOrUndefined(values[0]),
          defer(() => this.fetchPartialConversionQuote(values, termPolicy, paFaceAmount)),
          of(this.defaultPremium)
        )
      )
    );
  }

  private fetchTermPremium(
    [
      dob,
      gender,
      healthClass,
      contractState,
      coverageTerm,
      faceAmount,
      ecp,
      paymentFrequency,
      waiverOfPremium,
      product,
      flatExtras,
      type,
      effectiveDate,
      termLifeHealthClass,
      productPremiumVersion,
    ]: any[],
    previewTermRepriceDate?: Date,
    productPremiumVersionOverride?: string
  ): Observable<ProductQuoteResponse> {
    flatExtras = (flatExtras || []).map((fe) => {
      return { ruleCode: fe.ruleCode, name: fe.name, amount: fe.amount };
    });
    const ratingCode = termLifeHealthClass ? RateClasses[termLifeHealthClass].code : RateClasses[healthClass].code;
    const asOfDate = previewTermRepriceDate ? previewTermRepriceDate : Utils.now();
    const useProductPremiumVersion = productPremiumVersionOverride
      ? productPremiumVersionOverride
      : productPremiumVersion;
    const request: PremiumRequest = {
      product: product,
      productType: Product.productTypes.Term.code,
      gender,
      ratingCode,
      dob,
      effectiveDate: effectiveDate || Utils.now(),
      state: contractState,
      term: coverageTerm,
      face: faceAmount,
      flatExtras,
      paymentFrequency,
      waiver: waiverOfPremium,
      asOfDate, // if null, will use effectiveDate
    };
    return this.fetchPremium(request, useProductPremiumVersion);
  }

  private fetchPartialConversionQuote(
    [faceAmount]: number[],
    termPolicy: CoverpathPolicy,
    paFaceAmount: number
  ): Observable<ProductQuoteResponse> {
    const now: Date = new Date();
    now.setHours(8);
    const request: PolicyAdminRequestPartialConversionQuotePayload = {
      policyNumber: termPolicy.policyNumber,
      conversionAmount: paFaceAmount - faceAmount,
      effectiveDate: Utils.dateToDateDMY(now),
      quote: true,
    };

    return this.fetchPartialConversionPremium(request, termPolicy.getChannelCode());
  }

  createWholePremiumSolve$(
    clientInformation: AbstractControl,
    wholeLifeQuote: AbstractControl,
    solveForm: AbstractControl
  ): Observable<[number, number]> {
    const controls = [
      clientInformation.get('dob'),
      clientInformation.get('gender'),
      clientInformation.get('healthClass'),
      clientInformation.get('contractState'),
      wholeLifeQuote.get('product'),
      wholeLifeQuote.get('paymentFrequency'),
      wholeLifeQuote.get('waiverOfPremium'),
      wholeLifeQuote.get('riders.gir.amount'),
      wholeLifeQuote.get('riders.gir.enabled'),
      wholeLifeQuote.get('riders.rtr.amount'),
      wholeLifeQuote.get('riders.rtr.enabled'),
      wholeLifeQuote.get('riders.alir.amount'),
      wholeLifeQuote.get('riders.alir.enabled'),
      wholeLifeQuote.get('riders.alir.plannedYears'),
      wholeLifeQuote.get('riders.alir.paymentFrequency'),
      wholeLifeQuote.get('uwFlatExtras'),
      wholeLifeQuote.get('type'),
      wholeLifeQuote.get('effectiveDate'),
      wholeLifeQuote.get('healthClass'),
      wholeLifeQuote.get('productPremiumVersion'),
      solveForm.get('desiredPremium'),
    ];
    return this.createPremiumSolve(
      wholeLifeQuote,
      controls,
      this.validateWholePremiumSolve.bind(this),
      Product.productTypes.Whole.code
    );
  }

  createTermPremiumSolve$(
    clientInformation: AbstractControl,
    termQuote: AbstractControl,
    solveForm: AbstractControl
  ): Observable<[number, number]> {
    const controls = [
      clientInformation.get('dob'),
      clientInformation.get('gender'),
      clientInformation.get('healthClass'),
      clientInformation.get('contractState'),
      termQuote.get('product'),
      termQuote.get('paymentFrequency'),
      termQuote.get('waiverOfPremium'),
      termQuote.get('ecp'),
      termQuote.get('coverageTerm'),
      termQuote.get('ecp'), // these are dummy spacers
      termQuote.get('ecp'),
      termQuote.get('ecp'),
      termQuote.get('ecp'),
      termQuote.get('ecp'),
      termQuote.get('ecp'), // end spacers
      termQuote.get('uwFlatExtras'),
      termQuote.get('type'),
      termQuote.get('effectiveDate'),
      termQuote.get('healthClass'),
      termQuote.get('productPremiumVersion'),
      solveForm.get('desiredPremium'),
    ];
    return this.createPremiumSolve(
      termQuote,
      controls,
      this.validateTermPremiumSolve.bind(this),
      Product.productTypes.Term.code
    );
  }

  private createPremiumSolve(
    controlQuote: AbstractControl,
    controls: AbstractControl[],
    validator,
    productType
  ): Observable<[number, number]> {
    // map each control to an observable
    const observables = controls.map((control) =>
      combineLatest(
        control.valueChanges.pipe(startWith(control.value)),
        control.statusChanges.pipe(startWith('VALID'))
      ).pipe(
        debounceTime(750),
        map(([value, status]) => value),
        distinctUntilChanged()
      )
    );

    return combineLatest(observables).pipe(
      // no need to fetch WL premium if WL quote is disabled
      filter(() => controlQuote.enabled),
      switchMap((values) =>
        iif(
          () => validator(values),
          defer(() => this.fetchPremiumSolve(values, productType)),
          combineLatest([of(0), of(0)])
        )
      )
    );
  }

  createWholeLifePremium$(
    clientInformation: AbstractControl,
    wholeLifeQuote: AbstractControl
  ): Observable<ProductQuoteResponse> {
    // controls on which premium calculation is dependent. order is important.
    const controls = [
      clientInformation.get('dob'),
      clientInformation.get('gender'),
      clientInformation.get('healthClass'),
      clientInformation.get('contractState'),
      wholeLifeQuote.get('product'),
      wholeLifeQuote.get('faceAmount'),
      wholeLifeQuote.get('paymentFrequency'),
      wholeLifeQuote.get('waiverOfPremium'),
      wholeLifeQuote.get('riders.gir.amount'),
      wholeLifeQuote.get('riders.gir.enabled'),
      wholeLifeQuote.get('riders.rtr.amount'),
      wholeLifeQuote.get('riders.rtr.enabled'),
      wholeLifeQuote.get('riders.alir.amount'),
      wholeLifeQuote.get('riders.alir.enabled'),
      wholeLifeQuote.get('riders.alir.plannedYears'),
      wholeLifeQuote.get('riders.alir.paymentFrequency'),
      wholeLifeQuote.get('riders.cir.enabled'),
      wholeLifeQuote.get('riders.cir.amount'),
      wholeLifeQuote.get('riders.cir.benefitDisbursementMethod'),
      wholeLifeQuote.get('riders.cir.monthlyIncreaseOption'),
      wholeLifeQuote.get('uwFlatExtras'),
      wholeLifeQuote.get('type'),
      wholeLifeQuote.get('effectiveDate'),
      wholeLifeQuote.get('healthClass'),
      wholeLifeQuote.get('productPremiumVersion'),
    ];

    // map each control to an observable
    const observables = controls.map((control) =>
      combineLatest(
        control.valueChanges.pipe(startWith(control.value)),
        control.statusChanges.pipe(startWith('VALID'))
      ).pipe(
        debounceTime(500),
        map(([value, status]) => (status === 'VALID' || status === 'DISABLED' ? value : undefined))
      )
    );

    return combineLatest(observables).pipe(
      // no need to fetch WL premium if WL quote is disabled
      filter(() => wholeLifeQuote.enabled),
      switchMap((values) =>
        iif(
          () => this.validateWhole(values),
          defer(() => this.fetchWholePremium(values)),
          of(this.defaultPremium)
        )
      )
    );
  }

  private fetchPremiumSolve(
    [
      dob,
      gender,
      healthClass,
      contractState,
      product,
      paymentFrequency,
      waiverOfPremium,
      girAmount,
      hasGirRider,
      rtrAmount,
      hasRtrRider,
      alirAmount,
      alirEnabled,
      alirPlannedYears,
      alirPaymentFrequency,
      flatExtras,
      type,
      effectiveDate,
      quoteHealthClass,
      productPremiumVersion,
      desiredPremium,
    ]: any[],
    productType: string
  ): Observable<[number, number]> {
    if (desiredPremium === 0) {
      return combineLatest([of(0), of(0)]);
    }
    flatExtras = (flatExtras || []).map((fe) => {
      return { ruleCode: fe.ruleCode, name: fe.name, amount: fe.amount };
    });
    const ratingCode = quoteHealthClass
      ? RateClasses[quoteHealthClass].code
      : CommonUtils.determineRatingClass(healthClass, 100000, product, productType).code;

    // if this is a term product, the mapping isn't exactly straight forward because of the use of the array.
    // hasGirRider is only a boolean for GIR if this is a whole product.
    const isTermProduct = productType === Product.productTypes.Term.code;
    const term = isTermProduct ? parseInt(hasGirRider) : 0;

    const request: PremiumRequest = {
      product,
      productType: productType,
      gender,
      ratingCode,
      dob,
      effectiveDate: effectiveDate || Utils.now(),
      state: contractState,
      term: !!Product.productSpecs[product].term ? Product.productSpecs[product].term : term,
      face: 0,
      flatExtras,
      waiver: waiverOfPremium,
      paymentFrequency,
      riders: [],
      asOfDate: Utils.now(),
    };
    if (!isTermProduct) {
      if (hasGirRider === true) {
        request.riders.push({ type: RiderType.GIR, amount: girAmount });
      }
      if (hasRtrRider === true) {
        request.riders.push({ type: RiderType.RTR, amount: rtrAmount });
      }
    }

    // Fetch a standard tobacco / non-tobacco rate code and
    // see if it's different than the current rate so we
    // can avoid unnecessary premium solves. This is only
    // needed for whole life
    let standardRequestResult: Observable<number>;
    if (!isTermProduct) {
      let newRatingCode: string = '0';

      if (!!quoteHealthClass) {
        newRatingCode = !!RateClasses[quoteHealthClass].smoker
          ? RateClasses.STANDARD_TOBACCO.code
          : RateClasses.STANDARD.code;
      } else {
        newRatingCode = !!CommonUtils.determineRatingClass(healthClass, 100000, product, productType).smoker
          ? RateClasses.STANDARD_TOBACCO.code
          : RateClasses.STANDARD.code;
      }
      // Confirm that the rating code is, indeed, different. If it is, then create a deferred observable to recalculate the
      // request for the standard rating in addition to the request rating just in case this is a low face amount
      if (request.ratingCode !== newRatingCode) {
        const standardRequest: PremiumRequest = { ...request };
        standardRequest.ratingCode = newRatingCode;

        standardRequestResult = defer(() =>
          this.fetchPremiumSolveAmount(standardRequest, productPremiumVersion, desiredPremium)
        );
      }
      // if the rating code is the same, then even for low face amounts we just use the face amount provided in the first request
      else {
        standardRequestResult = of(-1);
      }
    } else {
      standardRequestResult = of(-1);
    }

    return combineLatest([
      defer(() => this.fetchPremiumSolveAmount(request, productPremiumVersion, desiredPremium)),
      standardRequestResult,
    ]);
  }

  private fetchPremiumSolveAmount(
    request: PremiumRequest,
    productPremiumVersion,
    desiredPremium?: number
  ): Observable<number> {
    return concat(
      // add some 'cancelable' delay so as to not flood the server
      timer(500).pipe(
        tap(() => this.loadingPremiumSolve$.next(true)),
        switchMap(() => this.controller.fetchQuotePremiumSolve(request, desiredPremium)),
        // in case of error, default to 0. Catching error is also important so as to not close the subscription.
        catchError((error) => of(0)),
        // end loading
        finalize(() => this.loadingPremiumSolve$.next(false))
      )
    );
  }

  private fetchWholePremium([
    dob,
    gender,
    healthClass,
    contractState,
    product,
    faceAmount,
    paymentFrequency,
    waiverOfPremium,
    girAmount,
    hasGirRider,
    rtrAmount,
    hasRtrRider,
    alirAmount,
    alirEnabled,
    alirPlannedYears,
    alirPaymentFrequency,
    cirEnabled,
    cirAmount,
    cirAccelerationPercent,
    cirMonthlyIncreaseOption,
    flatExtras,
    type,
    effectiveDate,
    wholeLifeHealthClass,
    productPremiumVersion,
  ]: any[]): Observable<ProductQuoteResponse> {
    flatExtras = (flatExtras || []).map((fe) => {
      return { ruleCode: fe.ruleCode, name: fe.name, amount: fe.amount };
    });
    const faceAndRtrAmount = hasRtrRider ? faceAmount + rtrAmount : faceAmount;
    const ratingCode = wholeLifeHealthClass
      ? RateClasses[wholeLifeHealthClass].code
      : CommonUtils.determineRatingClass(healthClass, faceAndRtrAmount, product, Product.productTypes.Whole.code).code;
    const request: PremiumRequest = {
      product,
      productType: Product.productTypes.Whole.code,
      gender,
      ratingCode,
      dob,
      effectiveDate: effectiveDate || Utils.now(),
      state: contractState,
      term: Product.productSpecs[product].term,
      face: faceAmount,
      flatExtras,
      waiver: waiverOfPremium,
      paymentFrequency,
      riders: [],
      asOfDate: Utils.now(),
    };
    if (hasGirRider) {
      request.riders.push({ type: RiderType.GIR, amount: girAmount });
    }
    if (hasRtrRider) {
      request.riders.push({ type: RiderType.RTR, amount: rtrAmount });
    }
    if (cirEnabled) {
      request.riders.push({
        type: RiderType.CIR,
        amount: cirAmount,
        accelerationPercent: cirAccelerationPercent,
        monthlyIncreaseOption: cirMonthlyIncreaseOption,
      });
    }
    return this.fetchPremium(request, productPremiumVersion);
  }

  private fetchPremium(request: PremiumRequest, productPremiumVersion): Observable<ProductQuoteResponse> {
    return concat(
      // send null premium and trigger loading
      of({ ...this.defaultPremium, base: null }).pipe(tap(() => this._loading$.next(true))),
      // add some 'cancelable' delay so as to not flood the server
      timer(500).pipe(
        switchMap(() => this.controller.publicFetchProductQuote(request, null, productPremiumVersion)),
        // in case of error, default to 0. Catching error is also important so as to not close the subscription.
        catchError((error) => of(this.defaultPremium)),
        // end loading
        finalize(() => this._loading$.next(false))
      )
    );
  }

  private fetchPartialConversionPremium(
    request: PolicyAdminRequestPartialConversionQuotePayload,
    channel: string
  ): Observable<ProductQuoteResponse> {
    return concat(
      // send null premium and trigger loading
      of({ ...this.defaultPremium, base: null }).pipe(tap(() => this._loading$.next(true))),
      // add some 'cancelable' delay so as to not flood the server
      timer(500).pipe(
        switchMap(() => this.controller.publicFetchPartialConversionQuote(request, channel)),
        // in case of error, default to 0. Catching error is also important so as to not close the subscription.
        catchError((error) => of(this.defaultPremium)),
        // end loading
        finalize(() => this._loading$.next(false))
      )
    );
  }

  createInitialInvoiceForBackdatingTermlife$(
    clientInformation: AbstractControl,
    termQuote: AbstractControl,
    policyNumber: string
  ): Observable<InitialInvoice> {
    // controls on which premium calculation is dependent. order is important.
    const controls = [
      clientInformation.get('dob'),
      clientInformation.get('gender'),
      clientInformation.get('healthClass'),
      clientInformation.get('contractState'),
      termQuote.get('coverageTerm'),
      termQuote.get('faceAmount'),
      termQuote.get('ecp'),
      termQuote.get('paymentFrequency'),
      termQuote.get('waiverOfPremium'),
      termQuote.get('product'),
      termQuote.get('uwFlatExtras'),
      termQuote.get('type'),
      termQuote.get('effectiveDate'),
      termQuote.get('healthClass'),
      termQuote.get('isSaveAge'),
      termQuote.get('productPremiumVersion'),
    ];

    // map each control to an observable
    const observables = controls.map((control) =>
      combineLatest(
        control.valueChanges.pipe(startWith(control.value)),
        control.statusChanges.pipe(startWith('VALID'))
      ).pipe(
        debounceTime(500),
        map(([value, status]) => (status === 'VALID' || status === 'DISABLED' ? value : undefined)),
        distinctUntilChanged()
      )
    );

    return combineLatest(observables).pipe(
      // no need to fetch  first payment or initial invoice if WL quote is disabled
      filter(() => termQuote.enabled),
      switchMap((values) =>
        iif(
          () => this.validateTerm(values),
          defer(() => this.fetchInitialInvoiceTerm(values, policyNumber)),
          of(this.initialInvoice)
        )
      )
    );
  }

  private fetchInitialInvoiceTerm(
    [
      dob,
      gender,
      healthClass,
      contractState,
      coverageTerm,
      faceAmount,
      ecp,
      paymentFrequency,
      waiverOfPremium,
      product,
      flatExtras,
      type,
      effectiveDate,
      termLifeHealthClass,
      isSaveAge,
      productPremiumVersion,
    ]: any[],
    policyNumber
  ): Observable<InitialInvoice> {
    if (type === POLICY_TYPES.AMEND_REMOVE_SECONDARY || !isSaveAge) {
      return of(this.initialInvoice);
    }
    flatExtras = (flatExtras || []).map((fe) => {
      return { ruleCode: fe.ruleCode, name: fe.name, amount: fe.amount };
    });
    const ratingCode = termLifeHealthClass
      ? RateClasses[termLifeHealthClass].code
      : CommonUtils.determineRatingClass(healthClass, faceAmount, product, Product.productTypes.Term.code).code;

    const request: PremiumRequest = {
      product: ecp ? Product.products.CASTermECP.code : Product.products.CASTerm.code,
      productType: Product.productTypes.Term.code,
      gender,
      ratingCode,
      dob,
      effectiveDate: effectiveDate || moment(Utils.now()).startOf('day').toDate(),
      asOfDate: moment(Utils.now()).startOf('day').toDate(),
      state: contractState,
      term: coverageTerm,
      face: faceAmount,
      flatExtras,
      paymentFrequency,
      waiver: waiverOfPremium,
    };
    return this.fetchInitialInvoiceFinallyTerm(request, productPremiumVersion, policyNumber);
  }

  private fetchInitialInvoiceFinallyTerm(
    request: PremiumRequest,
    productPremiumVersion,
    policyNumber
  ): Observable<InitialInvoice> {
    return concat(
      // // send null premium and trigger loading
      of({ ...this.initialInvoice }),
      // add some 'cancelable' delay so as to not flood the server
      timer(500).pipe(
        switchMap(() =>
          this.controller.accountCenterController.getInitialInvoiceInfo(policyNumber, request, productPremiumVersion)
        ),
        // in case of error, default to 0. Catching error is also important so as to not close the subscription.
        catchError((error) => of(this.initialInvoice))
      )
    );
  }

  createInitialInvoiceForBackdatingWholelife$(
    clientInformation: AbstractControl,
    wholeLifeQuote: AbstractControl,
    policyNumber: string
  ): Observable<InitialInvoice> {
    // controls on which premium calculation is dependent. order is important.
    const controls = [
      clientInformation.get('dob'),
      clientInformation.get('gender'),
      clientInformation.get('healthClass'),
      clientInformation.get('contractState'),
      wholeLifeQuote.get('product'),
      wholeLifeQuote.get('faceAmount'),
      wholeLifeQuote.get('paymentFrequency'),
      wholeLifeQuote.get('waiverOfPremium'),
      wholeLifeQuote.get('riders.gir.amount'),
      wholeLifeQuote.get('riders.gir.enabled'),
      wholeLifeQuote.get('riders.rtr.amount'),
      wholeLifeQuote.get('riders.rtr.enabled'),
      wholeLifeQuote.get('uwFlatExtras'),
      wholeLifeQuote.get('type'),
      wholeLifeQuote.get('effectiveDate'),
      wholeLifeQuote.get('healthClass'),
      wholeLifeQuote.get('isSaveAge'),
      wholeLifeQuote.get('productPremiumVersion'),
    ];

    // map each control to an observable
    const observables = controls.map((control) =>
      combineLatest(
        control.valueChanges.pipe(startWith(control.value)),
        control.statusChanges.pipe(startWith('VALID'))
      ).pipe(
        debounceTime(500),
        map(([value, status]) => (status === 'VALID' || status === 'DISABLED' ? value : undefined)),
        distinctUntilChanged()
      )
    );

    return combineLatest(observables).pipe(
      // no need to fetch  first payment or initial invoice if WL quote is disabled
      filter(() => wholeLifeQuote.enabled),
      switchMap((values) =>
        iif(
          () => this.validateWhole(values),
          defer(() => this.fetchInitialInvoiceWhole(values, policyNumber)),
          of(this.initialInvoice)
        )
      )
    );
  }

  private fetchInitialInvoiceWhole(
    [
      dob,
      gender,
      healthClass,
      contractState,
      product,
      faceAmount,
      paymentFrequency,
      waiverOfPremium,
      girAmount,
      girEnabled,
      rtrAmount,
      rtrEnabled,
      flatExtras,
      type,
      effectiveDate,
      wholeLifeHealthClass,
      isSaveAge,
      productPremiumVersion,
    ]: any[],
    policyNumber
  ): Observable<InitialInvoice> {
    if (type === POLICY_TYPES.AMEND_REMOVE_PRIMARY || !isSaveAge) {
      return of(this.initialInvoice);
    }

    flatExtras = (flatExtras || []).map((fe) => {
      return { ruleCode: fe.ruleCode, name: fe.name, amount: fe.amount };
    });
    const faceAndRtrAmount = rtrEnabled ? faceAmount + rtrAmount : faceAmount;
    const ratingCode = wholeLifeHealthClass
      ? RateClasses[wholeLifeHealthClass].code
      : CommonUtils.determineRatingClass(healthClass, faceAndRtrAmount, product, Product.productTypes.Whole.code).code;

    const request: PremiumRequest = {
      product,
      productType: Product.productTypes.Whole.code,
      gender,
      ratingCode,
      dob,
      effectiveDate: effectiveDate || moment(Utils.now()).startOf('day').toDate(),
      asOfDate: moment(Utils.now()).startOf('day').toDate(),
      state: contractState,
      term: Product.productSpecs[product].term,
      face: faceAmount,
      flatExtras,
      waiver: waiverOfPremium,
      paymentFrequency,
      riders: [],
    };
    if (girEnabled) {
      request.riders.push({ type: 'GIR', amount: girAmount });
    }
    if (rtrEnabled) {
      request.riders.push({ type: 'RTR', amount: rtrAmount });
    }

    return this.fetchInitialInvoiceFinallyWhole(request, productPremiumVersion, policyNumber);
  }

  private fetchInitialInvoiceFinallyWhole(
    request: PremiumRequest,
    productPremiumVersion,
    policyNumber
  ): Observable<InitialInvoice> {
    return concat(
      // // send null premium and trigger loading
      of({ ...this.initialInvoice }),
      // add some 'cancelable' delay so as to not flood the server
      timer(500).pipe(
        switchMap(() =>
          this.controller.accountCenterController.getInitialInvoiceInfo(policyNumber, request, productPremiumVersion)
        ),
        // in case of error, default to 0. Catching error is also important so as to not close the subscription.
        catchError((error) => of(this.initialInvoice))
      )
    );
  }
}
